import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Chip, Box } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

/**
 * ReviewQueueGrid Component
 * Display pending AI drafts for approval
 */

interface EvidenceRef {
  refType: "CGM_EVENT" | "MEAL_ENTRY" | "CONTEXT_NOTE" 
         | "LAB_RESULT" | "PROTOCOL_VERSION" | "MESSAGE_RESPONSE";
  refId: string;
  label?: string;
  meta?: Record<string, unknown>;
}

interface MessageDraft {
  id: string;
  patientId: string;
  patientName: string;
  draftContent: string;
  rationale?: string;
  evidenceRefs: EvidenceRef[];
  status: "PENDING_REVIEW" | "APPROVED" | "REJECTED";
  createdAt: string;
}

interface ReviewQueueGridProps {
  drafts: MessageDraft[];
  onDraftClick: (draft: MessageDraft) => void;
}

export const ReviewQueueGrid: React.FC<ReviewQueueGridProps> = ({
  drafts,
  onDraftClick
}) => {
  const columns: GridColDef[] = [
    {
      field: 'patientName',
      headerName: 'Patient',
      width: 180
    },
    {
      field: 'draftContent',
      headerName: 'Message Preview',
      width: 400,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', py: 1 }}>
          {params.value.substring(0, 100)}...
        </Box>
      )
    },
    {
      field: 'evidenceRefs',
      headerName: 'Evidence',
      width: 120,
      renderCell: (params) => (
        <Chip label={`${params.value.length} refs`} size="small" />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 150,
      valueFormatter: (params) =>
        formatDistanceToNow(new Date(params.value as string), { addSuffix: true })
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const color = params.value === "PENDING_REVIEW" ? "warning" : "default";
        return <Chip label={params.value as string} size="small" color={color} />;
      }
    }
  ];

  return (
    <DataGrid
      rows={drafts}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 10 }
        }
      }}
      pageSizeOptions={[10, 25, 50]}
      autoHeight
      disableRowSelectionOnClick
      onRowClick={(params) => onDraftClick(params.row as MessageDraft)}
      sx={{
        '& .MuiDataGrid-row': {
          cursor: 'pointer'
        }
      }}
    />
  );
};
